/**
 * HiddenRestartTrigger Component
 *
 * An invisible corner area that triggers app restart on long-press (3 seconds).
 * Used for emergency on-device restart capability without needing admin dashboard.
 *
 * Placement: Bottom-right corner of the flight board
 * Size: 80x80 pixels (small enough to be unobtrusive, large enough to tap)
 * Activation: Press and hold for 3 seconds
 *
 * Shows a subtle progress indicator while holding, then confirms before restarting.
 */
import React, { useState, useRef, useCallback } from 'react';
import { getIsAndroid } from '../config/config';
import { triggerManualRestart } from '../services/androidKiosk';

// Long-press duration in milliseconds
const LONG_PRESS_DURATION = 3000;

// Trigger zone size in pixels
const TRIGGER_SIZE = 80;

const HiddenRestartTrigger = () => {
  const [pressing, setPressing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);
  const pressTimerRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const startTimeRef = useRef(null);

  const handlePressStart = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    console.log('🔄 Hidden restart trigger: Press started');
    setPressing(true);
    setProgress(0);
    startTimeRef.current = Date.now();

    // Update progress every 50ms for smooth visual feedback
    progressIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const currentProgress = Math.min((elapsed / LONG_PRESS_DURATION) * 100, 100);
      setProgress(currentProgress);
    }, 50);

    // Set timer for long-press completion
    pressTimerRef.current = setTimeout(() => {
      console.log('🔄 Hidden restart trigger: Long-press completed');
      clearInterval(progressIntervalRef.current);
      setPressing(false);
      setProgress(0);
      setShowConfirm(true);
    }, LONG_PRESS_DURATION);
  }, []);

  const handlePressEnd = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }

    setPressing(false);
    setProgress(0);
  }, []);

  const handleConfirmRestart = useCallback(async () => {
    console.log('🔄 Restart confirmed by user');
    setShowConfirm(false);

    try {
      await triggerManualRestart('hidden_corner_trigger');
    } catch (error) {
      console.error('❌ Manual restart failed:', error);
      // Fallback to page reload
      window.location.reload();
    }
  }, []);

  const handleCancelRestart = useCallback(() => {
    console.log('🔄 Restart cancelled by user');
    setShowConfirm(false);
  }, []);

  // Only render on Android devices (guard placed after all hooks)
  const isAndroid = getIsAndroid();
  if (!isAndroid) {
    return null;
  }

  return (
    <>
      {/* Hidden trigger zone - positioned in bottom-right corner */}
      <div
        style={{
          position: 'fixed',
          bottom: 0,
          right: 0,
          width: TRIGGER_SIZE,
          height: TRIGGER_SIZE,
          zIndex: 9999,
          cursor: 'default',
          touchAction: 'none',
          // Slightly visible when pressing for feedback
          backgroundColor: pressing ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
          transition: 'background-color 0.2s ease',
        }}
        onTouchStart={handlePressStart}
        onTouchEnd={handlePressEnd}
        onTouchCancel={handlePressEnd}
        onMouseDown={handlePressStart}
        onMouseUp={handlePressEnd}
        onMouseLeave={handlePressEnd}
      >
        {/* Progress indicator - only visible while pressing */}
        {pressing && (
          <div
            style={{
              position: 'absolute',
              bottom: 10,
              right: 10,
              width: 60,
              height: 60,
              borderRadius: '50%',
              border: '3px solid rgba(255, 255, 255, 0.3)',
              background: `conic-gradient(
                rgba(255, 200, 0, 0.8) ${progress * 3.6}deg,
                transparent ${progress * 3.6}deg
              )`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              style={{
                width: 50,
                height: 50,
                borderRadius: '50%',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: 12,
                fontWeight: 'bold',
              }}
            >
              {Math.ceil((LONG_PRESS_DURATION - (progress / 100 * LONG_PRESS_DURATION)) / 1000)}s
            </div>
          </div>
        )}
      </div>

      {/* Confirmation modal */}
      {showConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              backgroundColor: '#1a1a2e',
              borderRadius: 12,
              padding: 32,
              maxWidth: 400,
              width: '90%',
              textAlign: 'center',
              border: '2px solid #ffd200',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
            }}
          >
            <div
              style={{
                fontSize: 48,
                marginBottom: 16,
              }}
            >
              🔄
            </div>
            <h2
              style={{
                color: '#fff',
                margin: '0 0 16px 0',
                fontSize: 24,
                fontWeight: 600,
              }}
            >
              Restart App?
            </h2>
            <p
              style={{
                color: '#aaa',
                margin: '0 0 24px 0',
                fontSize: 16,
                lineHeight: 1.5,
              }}
            >
              The app will close and restart. This takes about 5-10 seconds.
            </p>
            <div
              style={{
                display: 'flex',
                gap: 16,
                justifyContent: 'center',
              }}
            >
              <button
                onClick={handleCancelRestart}
                style={{
                  padding: '12px 32px',
                  fontSize: 18,
                  fontWeight: 600,
                  backgroundColor: '#333',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  minWidth: 120,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRestart}
                style={{
                  padding: '12px 32px',
                  fontSize: 18,
                  fontWeight: 600,
                  backgroundColor: '#ffd200',
                  color: '#000',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  minWidth: 120,
                }}
              >
                Restart
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HiddenRestartTrigger;
